import { useState, type ReactNode } from 'react';
import { Redirect, useLocation } from 'wouter';
import { PASSWORD_MIN } from '@sc/shared/domain';
import { api, ApiError } from '../lib/api';
import { Brand } from '../components/Brand';
import { homeFor, useAuth, type SessionUser } from './AuthContext';

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <main className="auth-page">
      <div className="auth-card">
        <Brand className="auth-brand" />
        {children}
      </div>
    </main>
  );
}

/**
 * Marca que a pessoa acabou de trocar a senha, para o login avisar. Fica em memória: o `signOut` esvazia
 * a sessão e a tela de troca redireciona sozinha para `/entrar`, então não dá para levar o aviso na URL.
 */
let senhaAcabouDeMudar = false;

export function LoginPage() {
  const { user, signIn } = useAuth();
  // Só lê: a tela monta mais de uma vez (StrictMode e redirecionamentos). O aviso some quando a pessoa tenta entrar.
  const [avisoSenha] = useState(() => senhaAcabouDeMudar);
  const [, navigate] = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user) return <Redirect to={user.mustChangePassword ? '/trocar-senha' : homeFor(user)} />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    senhaAcabouDeMudar = false;
    setBusy(true);
    setError(null);
    try {
      const u = await signIn(email, password);
      navigate(u.mustChangePassword ? '/trocar-senha' : homeFor(u));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard>
      <h1>Entrar no painel</h1>
      {avisoSenha && (
        <p className="alert alert-info" role="status">
          Senha criada. Entre de novo com a nova senha.
        </p>
      )}
      <form className="form" method="post" onSubmit={submit}>
        <div className="block">
          <label className="label" htmlFor="email">
            E-mail
          </label>
          <input
            id="email"
            className="control"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="block">
          <label className="label" htmlFor="password">
            Senha
          </label>
          <input
            id="password"
            className="control"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && (
          <p className="alert alert-error" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
      <p className="small">Esqueceu a senha? Peça ao administrador para redefinir.</p>
    </AuthCard>
  );
}

export function ChangePasswordPage() {
  const { user, signOut, loading } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (!user) return <Redirect to="/entrar" />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < PASSWORD_MIN) return setError(`A nova senha precisa ter pelo menos ${PASSWORD_MIN} caracteres.`);
    if (next !== confirm) return setError('A confirmação não é igual à nova senha.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      // A senha mudou: encerra a sessão e pede o login de novo com a senha nova.
      senhaAcabouDeMudar = true;
      // Sem navigate: com a sessão vazia, esta tela redireciona sozinha para o login.
      await signOut();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível trocar a senha.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard>
      <h1>{user.mustChangePassword ? 'Crie a sua senha' : 'Trocar senha'}</h1>
      {user.mustChangePassword && (
        <p className="alert alert-info">Você entrou com uma senha temporária. Crie uma senha sua para continuar.</p>
      )}
      <form className="form" method="post" onSubmit={submit}>
        <div className="block">
          <label className="label" htmlFor="current">
            Senha atual
          </label>
          <input
            id="current"
            className="control"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div className="block">
          <label className="label" htmlFor="next">
            Nova senha (mínimo {PASSWORD_MIN} caracteres)
          </label>
          <input
            id="next"
            className="control"
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>
        <div className="block">
          <label className="label" htmlFor="confirm">
            Repita a nova senha
          </label>
          <input
            id="confirm"
            className="control"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        {error && (
          <p className="alert alert-error" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? 'Salvando…' : 'Salvar senha'}
        </button>
      </form>
    </AuthCard>
  );
}

/** Só renderiza para os papéis indicados; senão leva ao login ou à área certa. */
export function RequireRole({ roles, children }: { roles: SessionUser['role'][]; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p className="page-pad small">Carregando…</p>;
  if (!user) return <Redirect to="/entrar" />;
  if (user.mustChangePassword) return <Redirect to="/trocar-senha" />;
  if (!roles.includes(user.role)) return <Redirect to={homeFor(user)} />;
  return <>{children}</>;
}
